import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { keysToCamel, keysToSnake } from './case-converter';

// Token management
const TOKEN_KEY = 'saturn_access_token';
const REFRESH_TOKEN_KEY = 'saturn_refresh_token';

export const getAccessToken = () => {
  if (typeof window === 'undefined') return null;
  // Try localStorage first, then cookies
  return localStorage.getItem(TOKEN_KEY) || getCookie(TOKEN_KEY);
};

export const getRefreshToken = () => {
  if (typeof window === 'undefined') return null;
  // Try localStorage first, then cookies
  return localStorage.getItem(REFRESH_TOKEN_KEY) || getCookie(REFRESH_TOKEN_KEY);
};

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Create axios instance
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log initialization
if (typeof window !== 'undefined') {
  console.log('🌐 API Client initialized with baseURL:', apiClient.defaults.baseURL);
}

// Request interceptor - add auth token and convert request data to snake_case
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔑 API Request:', config.method?.toUpperCase(), config.url, 'with auth token');
    } else {
      console.warn('⚠️ API Request:', config.method?.toUpperCase(), config.url, 'WITHOUT auth token');
    }

    // Convert request body to snake_case for POST/PUT/PATCH
    // Skip conversion for FormData (file uploads)
    if (
      config.data &&
      ['post', 'put', 'patch'].includes(config.method?.toLowerCase() || '') &&
      !(config.data instanceof FormData)
    ) {
      config.data = keysToSnake(config.data);
    }

    return config;
  },
  (error: Error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor - convert response data to camelCase
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('✅ API Response:', response.config.method?.toUpperCase(), response.config.url, 'Status:', response.status);
    
    // Convert response data from snake_case to camelCase
    if (response.data) {
      response.data = keysToCamel(response.data);
    }

    if (response.config.url?.includes('/v2/schools') && !response.config.url?.includes('/v2/schools/')) {
      console.log('📊 Schools API Response:', {
        schoolsCount: Array.isArray(response.data) ? response.data.length : 0,
        total: response.headers['x-pagination-items'],
        userCount: response.headers['x-user-count'],
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    console.error('❌ API Error:', error.config?.method?.toUpperCase(), error.config?.url, 'Status:', error.response?.status, error.response?.data);

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearTokens();
        window.location.href = '/auth/v2/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/v3/auth/refresh`,
          {
            access_token: accessToken,
            refresh_token: refreshToken,
          }
        );

        const newAccessToken = data.access_token;
        const newRefreshToken = data.refresh_token;

        setTokens(newAccessToken, newRefreshToken);
        processQueue(null, newAccessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        clearTokens();
        window.location.href = '/auth/v2/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

